import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { IPurposeMissionBeliefs } from './IPurposeMissionBeliefs';
import { IPlanTree } from './IPlanTree';
import { IDomain } from './IDomain';
import { IStep } from './IStep';
import { PlanDomain } from './PlanDomain';
import { PlanStep } from './PlanStep';
import { PlanStepPage } from './PlanStepPage';
import { PlanCoordinate } from './plan-coordinate';

@Injectable()
export class PlanService {
  private baseUrl = environment.baseUrl;

  constructor(private http: HttpClient) { }

  getPlanTree(strategyId: number): Observable<IPlanTree> {
    return this.http.get(`${this.baseUrl}/strategies/${strategyId}/plan`)
      .pipe(map((planTree: IPlanTree) => this.enhanceTree(planTree)));
  }

  getPlanHasInitiatives(strategyId: number): Observable<any> {
    return this.http.get(`${this.baseUrl}/strategies/${strategyId}/plan/has-initiatives`);
  }

  toggleStepComplete(planTree: IPlanTree, planCoordinate: PlanCoordinate, strategyId: number): Observable<IPlanTree> {
    const step = planTree.getStep(planCoordinate);
    step.complete = !step.complete;
    return this.updateStepComplete(strategyId, step.stepId, step.complete);
  }

  getPurposeMissionBeliefs(strategyId: number): Observable<IPurposeMissionBeliefs> {
    return this.http.get(`${this.baseUrl}/strategies/${strategyId}/plan/priorities`)
    .pipe(
      map((pmb: IPurposeMissionBeliefs) => {
        pmb.purpose = pmb.purpose || '';
        pmb.mission = pmb.mission || '';
        pmb.beliefs = pmb.beliefs || '';
        return pmb;
    }));
  }

  consoleLogPlanTree(planTree: IPlanTree) {
    planTree.domains.forEach(domain => {
      console.log('consoleLogPlanTree domain:', domain);
      domain.steps.forEach(step => {
        console.log('consoleLogPlanTree step:', step);
        if (step.planStepPages) {
          step.planStepPages.forEach(page => console.log('consoleLogPlanTree page:', page));
        }
      });
    });
  }

  private updateStepComplete(strategyId: number, stepId: number, complete: boolean): Observable<IPlanTree> {
    return this.http.put(`${this.baseUrl}/strategies/${strategyId}/steps/${stepId}/complete`, JSON.stringify(complete),
      this.getOptions()).pipe(map((planTree: IPlanTree) => this.enhanceTree(planTree)));
  }

  private enhanceTree(planTree: IPlanTree) {
    // add functions to the plan tree
    planTree.forEachStep = (onStep: (step: IStep) => void, planDomain?: PlanDomain) => {
      const domains: IDomain[] = (planDomain && [planTree.domains.find(it => it.planDomain === planDomain)]) || planTree.domains;
      domains.forEach(d => d.steps.forEach(s => onStep(s)));
    };

    // TODO: Rip out manual completable step injection
    planTree.forEachStep(it => {
      if (it.step_value === 'JUDGE_SUCCESS_OF_ANNUAL_PLAN') {
        it.isCompletable = true;
        it.isGuide = false;
        it.step_type = 'COMPLETABLE';
      }
    });

    planTree.getStep = (planCoordinate: PlanCoordinate): IStep => {
      const domain = planTree.domains.find(it => it.planDomain === planCoordinate.planDomain);
      let step = (domain && domain.steps || []).find(it => it.planStep === planCoordinate.planStep);
      if (!step) {
        console.error('getStep: no associated step', planCoordinate.toString());
        step = {
          step_name: 'Null Object Step',
          step_value: undefined,
          step_type: undefined,
          complete: false,
          stepId: undefined,
          planStep: undefined,
          planStepPages: [],
          isCompletable: false,
          isGuide: false,
        };
      }
      return step;
    };

    planTree.withStep = (planCoordinate: PlanCoordinate, onStep: (step: IStep) => void) => {
      const step = planTree.getStep(planCoordinate);
      if (step) {
        onStep(step);
      }
    };

    planTree.getNextPlanCoordinate = (planCoordinate: PlanCoordinate) => {
      return planCoordinate.getNextStepCoordinate(it => !planTree.getStep(it).isGuide);
    };

    planTree.getPreviousPlanCoordinate = (planCoordinate: PlanCoordinate) => {
      return planCoordinate.getPreviousStepCoordinate(it => !planTree.getStep(it).isGuide);
    };

    this.mapEnums(planTree);
    planTree.forEachStep(it => {
      if (it.isCompletable === undefined) {
        it.isCompletable = it.step_type === 'COMPLETABLE';
      }
      if (it.isGuide === undefined) {
        it.isGuide = it.step_type === 'GUIDE';
      }
      it.planStepPages = (it.planStep === PlanStep.OUTPUTS)
        ? [PlanStepPage.AnnualPlan, PlanStepPage.ImplementationPlan, PlanStepPage.StrategyMap, PlanStepPage.TheoryOfAction] : [];
    });

    // this.consoleLogPlanTree(planTree);
    return planTree;
  }

  private mapEnums(planTree: IPlanTree) {
    // wire up the enum values
    planTree.domains.forEach(domain => {
      domain.planDomain = PlanDomain[domain.domain_value];
      domain.steps.forEach(step => step.planStep = PlanStep[step.step_value]);
    });
    return planTree;
  }

  private getOptions() {
    return {headers: new HttpHeaders({'Content-Type':  'application/json'})};
  }
}
